import { useMemo } from 'react'
import { useState } from 'react'
import { SectionHeader, RangeSlider, SelectField, AgronomistNote, CircularMeter } from '@/components/ui/CalcUI'

type SoilType = 'loamy' | 'sandy' | 'clay' | 'alkaline'

const SOIL_OPTIONS = [
  { value: 'loamy', label: 'Loamy (Ideal)' },
  { value: 'sandy', label: 'Sandy (High Leaching)' },
  { value: 'clay', label: 'Clay (Poor Drainage)' },
  { value: 'alkaline', label: 'Alkaline / Saline' },
]

export default function FertilizerNutrient() {
  const [treeAge, setTreeAge] = useState(5)
  const [canopyDiameter, setCanopyDiameter] = useState(4)
  const [soilType, setSoilType] = useState<SoilType>('loamy')

  const nutrients = useMemo(() => {
    let N = Math.min(treeAge * 100, 1000)
    let P = Math.min(treeAge * 50, 500)
    let K = Math.min(treeAge * 100, 1000)

    // Canopy adjustment (10% per meter over 4m)
    const canopyFactor = 1 + Math.max(0, (canopyDiameter - 4) * 0.1)
    N = N * canopyFactor
    K = K * canopyFactor

    // Soil adjustments
    if (soilType === 'sandy') {
      N = N * 1.15
      K = K * 1.15
    }

    return {
      N: Math.round(N),
      P: Math.round(P),
      K: Math.round(K),
    }
  }, [treeAge, canopyDiameter, soilType])

  // Split application schedule
  const schedule = [
    {
      period: 'Post-Harvest / Pre-Flushing',
      month: 'June / July',
      icon: '🌿',
      color: '#16a34a',
      bgColor: '#f0fdf4',
      borderColor: '#86efac',
      n: nutrients.N * 0.5,
      p: nutrients.P * 1.0,
      k: nutrients.K * 0.5,
      nLabel: '50% N',
      pLabel: '100% P',
      kLabel: '50% K',
    },
    {
      period: 'Pre-Flowering',
      month: 'October / November',
      icon: '🌸',
      color: '#d97706',
      bgColor: '#fef3c7',
      borderColor: '#fbbf24',
      n: nutrients.N * 0.25,
      p: 0,
      k: nutrients.K * 0.25,
      nLabel: '25% N',
      pLabel: '—',
      kLabel: '25% K',
    },
    {
      period: 'Fruit Development',
      month: 'February / March',
      icon: '🥭',
      color: '#ea580c',
      bgColor: '#fff7ed',
      borderColor: '#fdba74',
      n: nutrients.N * 0.25,
      p: 0,
      k: nutrients.K * 0.25,
      nLabel: '25% N',
      pLabel: '—',
      kLabel: '25% K',
    },
  ]

  const isAlkaline = soilType === 'alkaline'
  const isSandy = soilType === 'sandy'

  return (
    <div>
      <SectionHeader
        badge="Calculator 2"
        h2="Mango Fertilizer & Nutrient Diagnostic Tool"
        description="Get precise N-P-K fertilizer recommendations and a split-application schedule based on tree age, canopy size, and soil type."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Inputs */}
        <div
          className="rounded-3xl border p-6"
          style={{ background: '#fff', borderColor: '#e8d9b0' }}
        >
          <h3 className="text-base font-bold mb-4" style={{ color: '#44403c' }}>🧪 Tree & Soil Parameters</h3>

          <RangeSlider
            label="Tree Age"
            value={treeAge}
            min={1}
            max={15}
            unit=" yrs"
            onChange={setTreeAge}
            tooltip="N = Age × 100g (capped at 1000g). P = Age × 50g (capped at 500g). K = Age × 100g (capped at 1000g). Based on ICAR mango nutrition guidelines."
          />

          <RangeSlider
            label="Canopy Diameter"
            value={canopyDiameter}
            min={1}
            max={10}
            unit=" m"
            onChange={setCanopyDiameter}
            tooltip="Larger canopy = greater leaf area = higher nutrient demand. N and K increase by 10% for every meter above 4m canopy diameter."
          />

          <SelectField
            label="Soil Type"
            value={soilType}
            options={SOIL_OPTIONS}
            onChange={(v) => setSoilType(v as SoilType)}
            tooltip="Soil type affects nutrient availability. Sandy soils require increased K and N due to leaching. Alkaline soils require micronutrient supplementation."
          />

          {isAlkaline && (
            <div
              className="mt-2 p-4 rounded-2xl border"
              style={{ background: '#fff7ed', borderColor: '#fb923c', color: '#9a3412' }}
            >
              <p className="text-sm font-bold">⚠️ Alkaline / Saline Soil Warning</p>
              <p className="text-sm mt-1 leading-relaxed">
                Apply <strong>iron chelate (EDTA-Fe)</strong> at 50–100g/tree and <strong>zinc foliar sprays</strong> (0.5% ZnSO₄) during flushing. High pH locks out micronutrients essential for fruit set.
              </p>
            </div>
          )}
          {isSandy && (
            <div
              className="mt-2 p-4 rounded-2xl border"
              style={{ background: '#f0fdf4', borderColor: '#86efac', color: '#14532d' }}
            >
              <p className="text-sm font-bold">🌱 Sandy Soil Adjustment Applied</p>
              <p className="text-sm mt-1">N and K increased by 15% to compensate for leaching losses.</p>
            </div>
          )}

          {/* Nutrient meters */}
          <div className="mt-6">
            <p className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: '#a16207' }}>
              Total Annual Nutrients
            </p>
            <div className="flex justify-around">
              <CircularMeter value={nutrients.N} max={1200} label="Nitrogen (N)" color="#16a34a" />
              <CircularMeter value={nutrients.P} max={600} label="Phosphorus (P₂O₅)" color="#0284c7" />
              <CircularMeter value={nutrients.K} max={1200} label="Potassium (K₂O)" color="#d97706" />
            </div>
          </div>
        </div>

        {/* Schedule */}
        <div className="space-y-4">
          <div
            className="rounded-3xl border p-5"
            style={{ background: '#fff', borderColor: '#e8d9b0' }}
          >
            <h3 className="text-base font-bold mb-4" style={{ color: '#44403c' }}>
              📅 Split-Application Schedule
            </h3>
            <div className="space-y-4">
              {schedule.map((s) => (
                <div
                  key={s.period}
                  className="rounded-2xl border p-4"
                  style={{ background: s.bgColor, borderColor: s.borderColor }}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xl">{s.icon}</span>
                    <div>
                      <p className="text-sm font-bold" style={{ color: s.color }}>{s.period}</p>
                      <p className="text-xs" style={{ color: '#78716c' }}>{s.month}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { nutrient: 'N (Nitrogen)', value: s.n, label: s.nLabel, color: '#16a34a' },
                      { nutrient: 'P₂O₅', value: s.p, label: s.pLabel, color: '#0284c7' },
                      { nutrient: 'K₂O', value: s.k, label: s.kLabel, color: '#d97706' },
                    ].map((n) => (
                      <div
                        key={n.nutrient}
                        className="rounded-xl p-2.5 text-center"
                        style={{ background: '#fff', border: `1px solid ${n.color}30` }}
                      >
                        <p className="text-xs font-semibold" style={{ color: n.color }}>{n.label}</p>
                        <p className="text-lg font-bold mt-0.5" style={{ color: '#1c1917' }}>
                          {n.value > 0 ? `${n.value.toFixed(0)}g` : '—'}
                        </p>
                        <p className="text-xs" style={{ color: '#a8a29e' }}>{n.nutrient}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <AgronomistNote
            text={`For a ${treeAge}-year-old tree with ${canopyDiameter}m canopy: Total N=${nutrients.N}g, P=${nutrients.P}g, K=${nutrients.K}g/year. Apply in 3 split doses for optimal uptake efficiency and reduced leaching loss.`}
            type="success"
          />
        </div>
      </div>
    </div>
  )
}
